import {
  Box,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Alert,
  Chip,
} from "@mui/material";
import React from "react";
import { StyledPaper } from "../../../customComponents/styled/styledPaper";
import { useFormik } from "formik";
import * as yup from "yup";
import { StyledTextField } from "../../../customComponents/styled/styledInputs";
import {
  StyledLoadingButton,
} from "../../../customComponents/styled/styledButtons";
import {
  useGetSchoolProfileQuery,
  useSetRecordingDeadlineMutation,
  useClearRecordingDeadlineMutation,
} from "../school/schoolApiSlice";
import { format, parseISO, isPast } from "date-fns";
import toast from "../../../../helpers/toast";

const validationSchema = yup.object({
  recording_deadline: yup
    .string()
    .required("Deadline date and time is required"),
});

function RecordingDeadline() {
  const { data: schoolInfo } = useGetSchoolProfileQuery();
  const [setDeadline, { isLoading: settingDeadline }] =
    useSetRecordingDeadlineMutation();
  const [clearDeadline, { isLoading: clearingDeadline }] =
    useClearRecordingDeadlineMutation();

  const currentDeadline = schoolInfo?.recording_deadline;
  const isLocked = currentDeadline && isPast(parseISO(currentDeadline));

  const formik = useFormik({
    initialValues: {
      recording_deadline: currentDeadline
        ? currentDeadline.slice(0, 16)
        : "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await setDeadline({
          recording_deadline: new Date(values.recording_deadline).toISOString(),
        }).unwrap();
        toast.success("Recording deadline set successfully");
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const handleClear = async () => {
    const confirm = window.confirm("Teachers will be able to record scores again. Remove deadline?");
    if (!confirm) return;
    try {
      await clearDeadline().unwrap();
      toast.success("Recording deadline has been removed");
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <StyledPaper>
      <Box className="paperHeader">
        <Typography variant="formHeading">Recording Deadline</Typography>
      </Box>
      <Box className="paperBody">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set a date and time after which teachers cannot record or modify
          scores, comments, or trait assessments. Only admins can make changes
          once the deadline passes. Remove the deadline to re-open recording.
        </Typography>

        {currentDeadline && (
          <Alert
            severity={isLocked ? "error" : "warning"}
            sx={{ mb: 2 }}
            action={
              <Chip
                label="Remove deadline"
                size="small"
                color="default"
                onClick={handleClear}
                disabled={clearingDeadline}
                sx={{ cursor: "pointer" }}
              />
            }
          >
            {isLocked
              ? `Recording is LOCKED. Deadline was ${format(
                  parseISO(currentDeadline),
                  "dd MMM yyyy, hh:mm a"
                )}.`
              : `Recording closes on ${format(
                  parseISO(currentDeadline),
                  "dd MMM yyyy, hh:mm a"
                )}.`}
          </Alert>
        )}

        {!currentDeadline && (
          <Alert severity="success" sx={{ mb: 2 }}>
            No deadline set — teachers can record freely.
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12} md={8}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">
                  Deadline Date &amp; Time
                </Typography>
                <StyledTextField
                  type="datetime-local"
                  variant="outlined"
                  id="recording_deadline"
                  name="recording_deadline"
                  value={formik.values.recording_deadline}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.recording_deadline &&
                    Boolean(formik.errors.recording_deadline)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.recording_deadline &&
                    formik.errors.recording_deadline}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} textAlign="right">
              <StyledLoadingButton
                loading={settingDeadline}
                type="submit"
              >
                {currentDeadline ? "Update Deadline" : "Set Deadline"}
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </StyledPaper>
  );
}

export default RecordingDeadline;
